import { AppShell, Loader } from "@mantine/core";

import Radar from "radar-sdk-js";
import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { authMiddleware } from "../api-clients/auth-middleware";
import { client } from "../api-clients/iwannacamp-api/client.gen";
import { getApiBaseUrl, isDevMode } from "../utils/debug";
import { startIpGeolocation } from "../utils/ipGeolocation";
import { shouldGeolocate } from "../utils/shouldGeolocate";
import classes from "./App.module.css";
import MapPlaceholder from "./map/MapPlaceholder";
import Navbar from "./navbar/Navbar";
import AdminRoute from "./ranger-station/AdminRoute";

// The /ranger-station admin console loads on demand. It was imported statically,
// so every anonymous visitor downloaded the whole admin app - and recharts,
// dnd-kit and the lightbox along with it - before first paint.
// maplibre-gl is ~263KB gzip - a third of first paint - and reaches the bundle
// only through this component. Deferring it lets the navbar and search UI
// paint first while the map streams in behind them.
const CampgroundMap = lazy(() => import("./map/CampgroundMap"));

const AdminLayout = lazy(() => import("./ranger-station/AdminLayout"));
const AdminDashboard = lazy(
  () => import("./ranger-station/dashboard/AdminDashboard")
);
const CampgroundsList = lazy(
  () => import("./ranger-station/campgrounds/CampgroundsList")
);
const ImageApprovalQueue = lazy(
  () => import("./ranger-station/images/ImageApprovalQueue")
);
const CampgroundImageReviewPage = lazy(
  () => import("./ranger-station/images/CampgroundImageReviewPage")
);
const CampgroundImageEditPage = lazy(
  () => import("./ranger-station/images/CampgroundImageEditPage")
);
const ReportList = lazy(() => import("./ranger-station/reports/ReportList"));
const ImportRunsList = lazy(
  () => import("./ranger-station/imports/ImportRunsList")
);
// Split from the list on purpose: this one pulls in recharts and maplibre for
// the trend chart and the change map, and the list has no use for either.
const ImportRunDetail = lazy(
  () => import("./ranger-station/imports/ImportRunDetail")
);

client.setConfig({
  baseUrl: getApiBaseUrl(),
});

// Apply auth middleware to automatically inject Firebase auth tokens
client.interceptors.request.use(authMiddleware);

Radar.initialize(
  isDevMode()
    ? "prj_test_pk_d69a1420ed2de6d79696d1151961541dfd6c12da"
    : "prj_live_pk_8068ea8346eaf78ac1a4aea435ba4b91c53a73a5"
);

// Fire the IP lookup now, while the ~311KB map chunk is still downloading, so
// the map can initialise at the right place instead of moving after load.
// This is only a head start: getInitialCenter() calls startIpGeolocation()
// itself the first time it actually needs a location, so skipping this call
// never loses correctness - it only loses the overlap with the chunk download.
// shouldGeolocate() is the single shared predicate (also used by
// CampgroundMap) for "is this request's result guaranteed to be discarded" -
// a hash that already supplies a center, a campground deep link, or the
// admin console, which never renders a map at all. Firing a third-party
// lookup whose result cannot possibly be used is waste with a privacy
// dimension, so we skip it here too rather than only at the point of use.
if (shouldGeolocate(window.location.pathname, window.location.hash)) {
  startIpGeolocation();
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Admin routes - separate from main app */}
        <Route
          path="/ranger-station"
          element={
            <AdminRoute>
              <Suspense fallback={<Loader m="xl" />}>
                <AdminLayout />
              </Suspense>
            </AdminRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="campgrounds" element={<CampgroundsList />} />
          <Route path="images" element={<ImageApprovalQueue />} />
          <Route
            path="images/campground/:campgroundSqid"
            element={<CampgroundImageReviewPage />}
          />
          <Route
            path="images/campground/:campgroundSqid/edit"
            element={<CampgroundImageEditPage />}
          />
          <Route path="reports" element={<ReportList />} />
          <Route path="imports" element={<ImportRunsList />} />
          <Route path="imports/:runId" element={<ImportRunDetail />} />
        </Route>

        {/* Main app - Navbar handles nested routing for /search, /explore, /campground/:id */}
        <Route
          path="/*"
          element={
            <AppShell>
              <AppShell.Main className={classes.appShellMain}>
                <Navbar />
                <Suspense fallback={<MapPlaceholder />}>
                  <CampgroundMap />
                </Suspense>
              </AppShell.Main>
            </AppShell>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
